import { lazy } from "react";

const RELOADED_AT_KEY = "vistaar_chunk_reloaded_at";

// How long after an automatic reload another one is refused. A stale tab is
// fixed by the first reload; a chunk still failing seconds later means the files
// are genuinely unreachable, and reloading again would only spin the tab. A real
// second deploy, hours later, falls outside the window and still recovers.
const RELOAD_COOLDOWN_MS = 10000;

// sessionStorage throws outright in some privacy modes, and a failed lazy import
// is the worst possible moment to add a second error.
const lastReloadAt = () => {
  try { return Number(sessionStorage.getItem(RELOADED_AT_KEY)) || 0; } catch { return 0; }
};
const markReloaded = () => {
  try { sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now())); } catch { /* ignore */ }
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * React.lazy that survives a deploy.
 *
 * Every build hashes its chunk filenames and the old files are replaced, so a
 * tab left open across a release asks for a chunk that no longer exists and the
 * router renders a raw ChunkLoadError stack instead of the page. One retry
 * covers an ordinary network blip; if the file is genuinely gone, reloading
 * fetches the new index.html and with it the new chunk names.
 *
 * Only one reload is allowed per cooldown window, so a build that is actually
 * broken surfaces its error (and with it the app's own error screen) instead of
 * trapping the tab in a reload loop.
 */
export default function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      try {
        await wait(400);
        return await importer();
      } catch (retryErr) {
        if (Date.now() - lastReloadAt() > RELOAD_COOLDOWN_MS) {
          markReloaded();
          window.location.reload();
          // Resolves never — the reload takes the page away.
          return new Promise(() => {});
        }
        throw retryErr;
      }
    }
  });
}
